import {useCallback, useEffect, useMemo, useState} from "react";
import {
	App as AntdApp,
	Button,
	Empty,
	Segmented,
	Select,
	Skeleton,
	Switch,
	Tag,
	Tooltip,
	Typography
} from "antd";
import {
	CheckCircleOutlined,
	EditOutlined,
	ExclamationCircleOutlined,
	HolderOutlined,
	ReloadOutlined,
	SaveOutlined,
	UndoOutlined
} from "@ant-design/icons";
import {DndContext, DragOverlay, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors} from "@dnd-kit/core";
import {SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy} from "@dnd-kit/sortable";
import {restrictToVerticalAxis} from "@dnd-kit/modifiers";
import {CSS} from "@dnd-kit/utilities";

import {listFieldRegistry, updateFieldEditorLayout} from "@modules/fields/api/fieldRegistryApi.js";
import {loadOrgTree} from "@modules/org/api/orgApi.js";
import {normalizeFieldKey} from "@modules/fields/utils/fieldRegistry.js";
import {toHexColor} from "@modules/fields/utils/fieldColors.js";
import {FIXED_PRODUCT_FIELD_KEYS, buildEditorColumns, hasSavedEditorLayout, sortByEditorSequence} from "@modules/fields/utils/editorLayout.js";

import "./FieldLayoutTab.css";

const {Text} = Typography;

const ALL_FIELDS_VALUE = "__all__";
const SKELETON_ROWS = 8;

const COLUMN_OPTIONS = [
	{label: "Auto", value: 0},
	{label: "1", value: 1},
	{label: "2", value: 2},
	{label: "3", value: 3}
];

// dnd-kit annuncia di default in inglese: qui i messaggi per chi usa tastiera e screen reader.
const DND_ACCESSIBILITY = {
	screenReaderInstructions: {
		draggable: "Premi spazio per prendere il campo, usa le frecce per spostarlo, spazio per lasciarlo, Esc per annullare."
	},
	announcements: {
		onDragStart: () => "Campo preso.",
		onDragOver: ({over}) => over ? "Campo in movimento." : "Campo fuori dalla lista.",
		onDragEnd: ({over}) => over ? "Campo spostato." : "Spostamento annullato.",
		onDragCancel: () => "Spostamento annullato."
	}
};

function rowId(field)
{
	return String(field?._id || field?.id || "");
}

function columnOf(field)
{
	return Number.isInteger(field?.editorColumn) ? field.editorColumn : 0;
}

function FieldRowContent({field, index, handleProps, disabled, onColumnChange})
{
	const isFixed = FIXED_PRODUCT_FIELD_KEYS.has(field?.key);
	return (
		<>
			<button
				type="button"
				className="field-layout-handle"
				disabled={disabled}
				aria-label={`Sposta ${field.name}`}
				{...handleProps}
			>
				<HolderOutlined/>
			</button>
			<span className="field-layout-position">{index + 1}</span>
			<span className="field-layout-name">
				<Tag color={toHexColor(field.color)} className="field-layout-tag">{field.name}</Tag>
				<span className="field-layout-key">{field.key}</span>
				{isFixed && (
					<Tooltip title="Nell'editor card sta sempre sotto il Seriale: qui la posizione conta solo per l'ordine dei tag XML, quando segue questo elenco">
						<Tag className="field-layout-fixed-tag">sotto il Seriale</Tag>
					</Tooltip>
				)}
			</span>
			<Segmented
				size="small"
				className="field-layout-column-picker"
				options={COLUMN_OPTIONS}
				value={columnOf(field)}
				disabled={disabled || isFixed}
				onChange={(value) => onColumnChange?.(field, value)}
			/>
		</>
	);
}

function SortableFieldRow({field, index, disabled, changed, linked, onColumnChange, onHover})
{
	const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id: rowId(field)});

	const style = {
		transform: CSS.Transform.toString(transform),
		transition
	};

	const className = [
		"field-layout-row",
		changed && "is-changed",
		linked && "is-linked",
		isDragging && "is-placeholder"
	].filter(Boolean).join(" ");

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={className}
			onMouseEnter={() => onHover(rowId(field))}
			onMouseLeave={() => onHover(null)}
		>
			<FieldRowContent
				field={field}
				index={index}
				disabled={disabled}
				onColumnChange={onColumnChange}
				handleProps={{...attributes, ...listeners}}
			/>
		</div>
	);
}

/**
 * Tab "Posizione campi": ordine globale + colonna dei campi prodotto nell'editor card,
 * valida per tutti i clienti. Non usa <Form> perché la pagina Impostazioni ne contiene
 * già uno e AntD non supporta form annidati.
 */
export default function FieldLayoutTab()
{
	const {message} = AntdApp.useApp();

	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState("");
	const [saving, setSaving] = useState(false);
	const [serverFields, setServerFields] = useState([]);
	const [draft, setDraft] = useState([]);
	const [orgNodes, setOrgNodes] = useState([]);
	const [selectedOrg, setSelectedOrg] = useState(ALL_FIELDS_VALUE);
	// Campi trascinati o con la colonna cambiata dall'ultimo salvataggio: servono a segnare
	// le righe toccate senza marcare quelle che scalano solo di posizione.
	const [touchedIds, setTouchedIds] = useState(() => new Set());
	const [activeId, setActiveId] = useState(null);
	const [hoveredId, setHoveredId] = useState(null);
	// Interruttore "usa questo ordine anche nell'XML": valore salvato e bozza, si salva con le posizioni.
	const [serverXmlFollows, setServerXmlFollows] = useState(false);
	const [xmlFollows, setXmlFollows] = useState(false);

	const sensors = useSensors(
		useSensor(PointerSensor, {activationConstraint: {distance: 4}}),
		useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
	);

	const applyServerFields = useCallback((fields, xmlFollowsEditorLayout) =>
	{
		const productFields = fields.filter((f) => !f?.bannerOnly);
		setServerFields(productFields);
		setDraft(sortByEditorSequence(productFields));
		setTouchedIds(new Set());
		setServerXmlFollows(xmlFollowsEditorLayout === true);
		setXmlFollows(xmlFollowsEditorLayout === true);
	}, []);

	const loadData = useCallback(async () =>
	{
		setLoading(true);
		setLoadError("");
		try
		{
			const [registryResult, nodes] = await Promise.all([listFieldRegistry(), loadOrgTree()]);

			if (registryResult.ok !== false)
				applyServerFields(Array.isArray(registryResult.fields) ? registryResult.fields : [], registryResult.xmlFollowsEditorLayout);
			else
				setLoadError(registryResult.error || "Impossibile caricare il catalogo campi");

			setOrgNodes(Array.isArray(nodes) ? nodes : []);
		}
		catch
		{
			setLoadError("Impossibile caricare il catalogo campi");
		}
		finally
		{
			setLoading(false);
		}
	}, [applyServerFields]);

	useEffect(() =>
	{
		loadData();
	}, [loadData]);

	// Un cliente selezionato che sparisce dopo un ricaricamento tornerebbe a un Select
	// con value orfano: lo si riallinea su "Tutti i campi".
	useEffect(() =>
	{
		if (selectedOrg === ALL_FIELDS_VALUE)
			return;

		if (!orgNodes.some((node) => String(node?._id) === String(selectedOrg)))
			setSelectedOrg(ALL_FIELDS_VALUE);
	}, [orgNodes, selectedOrg]);

	const serverOrder = useMemo(() => sortByEditorSequence(serverFields), [serverFields]);
	const layoutSaved = useMemo(() => hasSavedEditorLayout(serverFields), [serverFields]);

	const layoutDirty = useMemo(() =>
	{
		if (serverOrder.length !== draft.length)
			return true;

		return serverOrder.some((field, idx) =>
		{
			const current = draft[idx];
			return !current || rowId(current) !== rowId(field) || columnOf(current) !== columnOf(field);
		});
	}, [serverOrder, draft]);

	const isDirty = layoutDirty || xmlFollows !== serverXmlFollows;

	// Un campo toccato conta come modificato solo se è davvero diverso dal salvato: chi viene
	// trascinato e poi rimesso al suo posto non resta segnato.
	const changedIds = useMemo(() =>
	{
		const serverIndex = new Map(serverOrder.map((field, idx) => [rowId(field), idx]));
		const serverColumn = new Map(serverOrder.map((field) => [rowId(field), columnOf(field)]));
		const changed = new Set();
		draft.forEach((field, idx) =>
		{
			const id = rowId(field);
			if (touchedIds.has(id) && (serverIndex.get(id) !== idx || serverColumn.get(id) !== columnOf(field)))
				changed.add(id);
		});
		return changed;
	}, [draft, serverOrder, touchedIds]);

	// Esistono clienti omonimi (es. due "Supermercati"): il consorzio tra parentesi li distingue.
	const cooperativaOptions = useMemo(() =>
	{
		const cooperative = orgNodes.filter((node) => node?.type === "COOPERATIVA");
		const nameCounts = new Map();
		for (const node of cooperative)
		{
			const name = String(node?.name || "").toLowerCase();
			nameCounts.set(name, (nameCounts.get(name) || 0) + 1);
		}
		const nodeById = new Map(orgNodes.map((node) => [String(node?._id), node]));

		return cooperative
			.slice()
			.sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")))
			.map((node) =>
			{
				const parentName = nodeById.get(String(node.parentId))?.name;
				const isDuplicate = nameCounts.get(String(node?.name || "").toLowerCase()) > 1;
				return {
					label: isDuplicate && parentName ? `${node.name} (${parentName})` : node.name,
					value: String(node._id)
				};
			});
	}, [orgNodes]);

	const selectedNode = useMemo(() =>
	{
		if (selectedOrg === ALL_FIELDS_VALUE)
			return null;
		return orgNodes.find((node) => String(node?._id) === String(selectedOrg)) || null;
	}, [orgNodes, selectedOrg]);

	// Per l'anteprima editorPosition è l'indice nella bozza: l'ordine riflesso è subito
	// quello del trascinamento, senza attendere il salvataggio.
	const previewColumns = useMemo(() =>
	{
		const allowedKeys = selectedNode
			? new Set((selectedNode.allowedProductFields || [])
				.map((allowed) => normalizeFieldKey(allowed?.name || allowed?.key))
				.filter(Boolean))
			: null;

		const fields = draft
			.map((field, idx) => ({...field, editorPosition: idx}))
			.filter((field) => !allowedKeys || allowedKeys.has(field.key))
			.filter((field) => !FIXED_PRODUCT_FIELD_KEYS.has(field.key));

		return buildEditorColumns(fields);
	}, [draft, selectedNode]);

	const previewFieldCount = useMemo(() =>
		previewColumns.reduce((total, bucket) => total + bucket.length, 0),
	[previewColumns]);

	const activeIndex = useMemo(() => draft.findIndex((item) => rowId(item) === activeId), [draft, activeId]);

	const markTouched = useCallback((id) =>
	{
		setTouchedIds((prev) =>
		{
			if (prev.has(id))
				return prev;
			const next = new Set(prev);
			next.add(id);
			return next;
		});
	}, []);

	const handleDragStart = useCallback(({active}) =>
	{
		setActiveId(active.id);
	}, []);

	const handleDragEnd = useCallback(({active, over}) =>
	{
		setActiveId(null);
		if (!over || active.id === over.id)
			return;

		setDraft((prev) =>
		{
			const from = prev.findIndex((item) => rowId(item) === active.id);
			const to = prev.findIndex((item) => rowId(item) === over.id);
			if (from < 0 || to < 0)
				return prev;
			return arrayMove(prev, from, to);
		});
		markTouched(active.id);
	}, [markTouched]);

	const handleDragCancel = useCallback(() =>
	{
		setActiveId(null);
	}, []);

	const handleColumnChange = useCallback((field, value) =>
	{
		const id = rowId(field);
		setDraft((prev) => prev.map((item) => rowId(item) === id ? {...item, editorColumn: value} : item));
		markTouched(id);
	}, [markTouched]);

	const handleReset = useCallback(() =>
	{
		setDraft(sortByEditorSequence(serverFields));
		setTouchedIds(new Set());
		setXmlFollows(serverXmlFollows);
	}, [serverFields, serverXmlFollows]);

	const handleSave = useCallback(async () =>
	{
		setSaving(true);
		try
		{
			const result = await updateFieldEditorLayout(draft.map((field) => ({
				id: field._id || field.id,
				editorColumn: columnOf(field)
			})), {xmlFollowsEditorLayout: xmlFollows});

			if (result.ok !== false && Array.isArray(result.fields))
			{
				applyServerFields(result.fields, result.xmlFollowsEditorLayout);
				message.success("Posizioni salvate");
			}
			else
			{
				message.error(result.error || "Salvataggio non riuscito");
			}
		}
		finally
		{
			setSaving(false);
		}
	}, [draft, xmlFollows, message, applyServerFields]);

	const activeField = activeIndex >= 0 ? draft[activeIndex] : null;
	const changedCount = changedIds.size;

	let status;
	if (isDirty)
	{
		status = {
			tone: "dirty",
			icon: <EditOutlined/>,
			text: changedCount
				? `${changedCount} ${changedCount === 1 ? "campo modificato" : "campi modificati"}, non ancora salvati`
				: "Modifiche non salvate"
		};
	}
	else if (!layoutSaved)
	{
		status = {
			tone: "warning",
			icon: <ExclamationCircleOutlined/>,
			text: "Layout mai salvato: finché non salvi, i clienti mantengono l'ordine attuale."
		};
	}
	else
	{
		status = {tone: "saved", icon: <CheckCircleOutlined/>, text: "Tutte le posizioni sono salvate"};
	}

	if (loadError && !loading)
	{
		return (
			<div className="field-layout-tab">
				<div className="field-layout-error">
					<ExclamationCircleOutlined className="field-layout-error-icon"/>
					<div>
						<Text strong>{loadError}</Text>
						<Text type="secondary" className="field-layout-error-hint">Controlla la connessione e riprova.</Text>
					</div>
					<Button icon={<ReloadOutlined/>} onClick={loadData}>Riprova</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="field-layout-tab">
			<header className="field-layout-header">
				<h3 className="field-layout-title">Posizione dei campi nell'editor card</h3>
				<p className="field-layout-subtitle">
					Ordine e colonna valgono per tutti i clienti: ognuno vede solo i campi assegnati in /org.
					<br/>
					Nomi, dati e AI non cambiano.
				</p>
				<label className="field-layout-xml-toggle">
					<Switch
						checked={xmlFollows}
						onChange={setXmlFollows}
						disabled={loading || saving}
					/>
					<span className="field-layout-xml-text">
						<span className="field-layout-xml-label">Usa questo ordine anche nell'XML</span>
						<span className="field-layout-xml-hint">
							{xmlFollows
								? "I tag dentro ogni gabbia escono nell'ordine qui sotto, per tutti i clienti. Gli attributi della gabbia non cambiano."
								: "Spento: nell'XML i tag restano nell'ordine attuale di ogni cliente."}
						</span>
					</span>
				</label>
			</header>

			<div className="field-layout-grid">
				<section className="field-layout-panel">
					<div className="field-layout-panel-head">
						<span className="field-layout-panel-title">
							Ordine dei campi
							{!loading && <span className="field-layout-count">{draft.length}</span>}
						</span>
						<span className="field-layout-panel-hint">
							Trascina dalla maniglia per spostare. Auto mette il campo nella colonna meno piena.
						</span>
					</div>

					{loading ? (
						<div className="field-layout-list" aria-busy="true">
							{Array.from({length: SKELETON_ROWS}, (_, idx) => (
								<div key={idx} className="field-layout-row is-skeleton">
									<Skeleton.Input active size="small" block/>
								</div>
							))}
						</div>
					) : (
						<DndContext
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragStart={handleDragStart}
							onDragEnd={handleDragEnd}
							onDragCancel={handleDragCancel}
							modifiers={[restrictToVerticalAxis]}
							accessibility={DND_ACCESSIBILITY}
						>
							<SortableContext items={draft.map(rowId)} strategy={verticalListSortingStrategy}>
								<div className="field-layout-list">
									{draft.map((field, idx) => (
										<SortableFieldRow
											key={rowId(field)}
											field={field}
											index={idx}
											disabled={saving}
											changed={changedIds.has(rowId(field))}
											linked={hoveredId === rowId(field)}
											onColumnChange={handleColumnChange}
											onHover={setHoveredId}
										/>
									))}
								</div>
							</SortableContext>
							<DragOverlay dropAnimation={{duration: 180, easing: "cubic-bezier(0.16, 1, 0.3, 1)"}}>
								{activeField ? (
									<div className="field-layout-row is-overlay">
										<FieldRowContent field={activeField} index={activeIndex}/>
									</div>
								) : null}
							</DragOverlay>
						</DndContext>
					)}
				</section>

				<section className="field-layout-panel field-layout-preview-panel">
					<div className="field-layout-panel-head field-layout-preview-head">
						<span className="field-layout-panel-title">Anteprima editor card</span>
						<Select
							className="field-layout-org-select"
							value={selectedOrg}
							options={[
								{label: "Tutti i campi", value: ALL_FIELDS_VALUE},
								...cooperativaOptions
							]}
							onChange={setSelectedOrg}
							disabled={saving || loading}
							showSearch
							optionFilterProp="label"
							aria-label="Cliente dell'anteprima"
						/>
					</div>
					{!loading && previewFieldCount > 0 && (
						<span className="field-layout-panel-hint">
							{selectedNode
								? `${previewFieldCount} ${previewFieldCount === 1 ? "campo assegnato" : "campi assegnati"} a ${selectedNode.name}`
								: `Tutti i ${previewFieldCount} campi del catalogo`}
						</span>
					)}

					{loading ? (
						<Skeleton active paragraph={{rows: 6}} title={false}/>
					) : previewColumns.length ? (
						<div className="field-layout-preview">
							{previewColumns.map((bucket, idx) => (
								<div key={idx} className="field-layout-preview-column">
									{bucket.map((field) => (
										<div
											key={field.key}
											className={`field-layout-preview-field${hoveredId === rowId(field) ? " is-linked" : ""}`}
											onMouseEnter={() => setHoveredId(rowId(field))}
											onMouseLeave={() => setHoveredId(null)}
										>
											<span className="field-layout-preview-label">{field.name}</span>
											<span className="field-layout-preview-input" aria-hidden="true"/>
										</div>
									))}
								</div>
							))}
						</div>
					) : (
						<Empty
							image={Empty.PRESENTED_IMAGE_SIMPLE}
							className="field-layout-empty"
							description={selectedNode
								? "Nessun campo assegnato a questo cliente. I campi si assegnano in /org."
								: "Il catalogo campi è vuoto."}
						/>
					)}
				</section>
			</div>

			<div className={`field-layout-savebar is-${status.tone}`} role="status">
				<span className="field-layout-status">
					{!loading && <span className="field-layout-status-icon">{status.icon}</span>}
					{loading ? "Caricamento dei campi…" : status.text}
				</span>
				<span className="field-layout-actions">
					<Button icon={<UndoOutlined/>} onClick={handleReset} disabled={loading || saving || !isDirty}>
						Annulla modifiche
					</Button>
					{/* Mai salvato: si può confermare anche l'ordine proposto così com'è. */}
					<Button type="primary" icon={<SaveOutlined/>} onClick={handleSave} loading={saving} disabled={loading || (layoutSaved && !isDirty)}>
						Salva posizioni
					</Button>
				</span>
			</div>
		</div>
	);
}
